import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOne, getPaginated, post, patch, put, del } from '@/lib/apiV2';
import { schoolKeys } from '@/features/academic/api';
import type { PagedResult, School } from '@/features/auth/types';

// ============================================================================
// Settings console api hooks.
//
// This console COMPOSES existing module hooks (geo/fees/library) rather than
// re-declaring their fetch logic — see the re-export barrel at the bottom.
// Only org + schools + departments get NEW hooks here, since they have no
// home elsewhere. School list reuses academic's `useSchools` / `schoolKeys`
// to share the TanStack cache.
// ============================================================================

// ---- Query keys -------------------------------------------------------------

export const settingsKeys = {
  org: (orgId: string) => ['settings', 'org', orgId] as const,
  departments: (schoolId: string) => ['settings', 'departments', schoolId] as const,
};

// ---- JSONB decode helper ----------------------------------------------------
//
// GOTCHA (verified against gen.Organization.Settings / gen.School.Address):
// both jsonb columns are Go `[]byte`, and encoding/json serializes a bare
// []byte as a BASE64 STRING — not a JSON object. So on read these arrive as
// e.g. "eyJjaXR5IjoiUHVuZSJ9". On WRITE the handlers bind `json.RawMessage`,
// so we send a plain JSON object back. This decoder is read-side only and is
// defensive: it accepts a base64 string, a raw JSON string, an already-parsed
// object, or null/empty.
export function decodeJsonB<T extends object>(value: unknown): T | null {
  if (value == null) return null;
  if (typeof value === 'object') return value as T;
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (raw === '') return null;

  const tryParse = (s: string): T | null => {
    try {
      const parsed = JSON.parse(s);
      return parsed && typeof parsed === 'object' ? (parsed as T) : null;
    } catch {
      return null;
    }
  };

  // Base64 first (the actual wire shape), then fall back to raw JSON.
  try {
    const decoded = atob(raw);
    const fromB64 = tryParse(decoded);
    if (fromB64) return fromB64;
  } catch {
    // not valid base64 — fall through
  }
  return tryParse(raw);
}

// ---- Organization -----------------------------------------------------------

export interface Organization {
  id: string;
  code: string;
  name: string;
  timezone: string;
  currency: string;
  /** jsonb — base64 string on the wire. Advisory only; not surfaced in v1. */
  settings: string | null;
  created_at: string;
  updated_at: string;

  // ---- Slice 4 settings knobs (docs/design/completeness-lld.md) -----------
  /** Org logo, rendered in the app shell header (AppShellLegacy) when set. */
  logo_url: string | null;
  /** Hex accent color (e.g. "#1a73e8"), used alongside the logo in the shell. */
  brand_color: string | null;
  /**
   * Config-lock: when true, settings-mutating endpoints (org update, school
   * and department create/update/delete) reject with 423 CONFIG_LOCKED
   * unless the actor holds settings.lock_override (see useSetConfigLock).
   */
  config_locked: boolean;
  config_locked_at: string | null;
  config_locked_by: string | null;
  /**
   * Approval-depth fallback: the backend approval engine reads this ONLY
   * when no configured policy matches a request type (real enforcement, not
   * advisory — see internal/service/approval/service.go's evaluatePolicy).
   * null means "no fallback configured" (today's behavior: such a request
   * fails with APPROVAL_POLICY_NOT_FOUND until a catch-all policy exists).
   */
  default_approval_levels: number | null;
}

export interface UpdateOrgInput {
  name?: string;
  timezone?: string;
  currency?: string;
  /** Empty string clears the field; omit to leave unchanged. */
  logo_url?: string;
  /** Empty string clears the field; omit to leave unchanged. */
  brand_color?: string;
  default_approval_levels?: number;
}

export function useOrg(orgId: string | undefined) {
  return useQuery<Organization>({
    queryKey: settingsKeys.org(orgId ?? ''),
    queryFn: () => getOne<Organization>(`/orgs/${orgId}`),
    enabled: Boolean(orgId),
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateOrg(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation<Organization, Error, UpdateOrgInput>({
    mutationFn: (input) => patch<UpdateOrgInput, Organization>(`/orgs/${orgId}`, input),
    onSuccess: (org) => {
      queryClient.setQueryData(settingsKeys.org(orgId), org);
    },
  });
}

/**
 * Locks or unlocks org-wide settings mutation (Slice 4 config-lock). Gated
 * server-side on settings.lock_override — a distinct, stricter permission
 * than settings.manage (see backend org.Service.SetConfigLock).
 */
export function useSetConfigLock(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation<Organization, Error, boolean>({
    mutationFn: (locked) => put<{ locked: boolean }, Organization>(`/orgs/${orgId}/config-lock`, { locked }),
    onSuccess: (org) => {
      queryClient.setQueryData(settingsKeys.org(orgId), org);
    },
  });
}

// ---- Schools ----------------------------------------------------------------
//
// The /schools list already has a hook + cache in features/academic
// (`useSchools` / `schoolKeys.list()`). We reuse it (re-exported below) and
// only add the mutations here — each invalidates the shared list key. The
// list row carries `address` (base64 jsonb) even though the auth `School`
// type doesn't declare it, so the profile card reads it via `SchoolWithAddress`.

export interface SchoolWithAddress extends School {
  address?: string | null;
  updated_at?: string;
}

export interface SchoolAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  email?: string;
}

export interface CreateSchoolInput {
  code: string;
  name: string;
  address?: SchoolAddress;
}

export interface UpdateSchoolInput {
  name?: string;
  address?: SchoolAddress;
  is_active?: boolean;
}

export function useCreateSchool() {
  const queryClient = useQueryClient();
  return useMutation<SchoolWithAddress, Error, CreateSchoolInput>({
    mutationFn: (input) => post<CreateSchoolInput, SchoolWithAddress>('/schools', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schoolKeys.list() });
    },
  });
}

export function useUpdateSchool() {
  const queryClient = useQueryClient();
  return useMutation<SchoolWithAddress, Error, { id: string; patch: UpdateSchoolInput }>({
    mutationFn: ({ id, patch: body }) =>
      patch<UpdateSchoolInput, SchoolWithAddress>(`/schools/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schoolKeys.list() });
    },
  });
}

export function useDeleteSchool() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del(`/schools/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schoolKeys.list() });
    },
  });
}

// ---- Departments ------------------------------------------------------------

export type DepartmentWing = 'academic' | 'admin' | 'org';

export interface Department {
  id: string;
  org_id: string;
  school_id: string | null;
  wing: DepartmentWing;
  name: string;
  head_staff_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CreateDepartmentInput {
  wing: DepartmentWing;
  name: string;
  head_staff_id?: string | null;
}

export interface UpdateDepartmentInput {
  name?: string;
  wing?: DepartmentWing;
  head_staff_id?: string | null;
  is_active?: boolean;
}

export function useDepartments(schoolId: string | undefined) {
  return useQuery<PagedResult<Department>>({
    queryKey: settingsKeys.departments(schoolId ?? ''),
    queryFn: () =>
      getPaginated<Department>(`/schools/${schoolId}/departments`, { per_page: 100 }),
    enabled: Boolean(schoolId),
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateDepartment(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Department, Error, CreateDepartmentInput>({
    mutationFn: (input) =>
      post<CreateDepartmentInput, Department>(`/schools/${schoolId}/departments`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.departments(schoolId) });
    },
  });
}

export function useUpdateDepartment(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Department, Error, { id: string; patch: UpdateDepartmentInput }>({
    mutationFn: ({ id, patch: body }) =>
      patch<UpdateDepartmentInput, Department>(`/schools/${schoolId}/departments/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.departments(schoolId) });
    },
  });
}

export function useDeleteDepartment(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del(`/schools/${schoolId}/departments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.departments(schoolId) });
    },
  });
}

// ---- Reused module hooks (compose, don't re-fetch) --------------------------
//
// Per-school module settings live in their own feature's api.ts. The cards
// import them from HERE so there is one import surface and one shared cache.

export {
  useGeoConfig,
  useUpdateGeoConfig,
  MIN_GEOFENCE_RADIUS_M,
  MAX_GEOFENCE_RADIUS_M,
  DEFAULT_GEOFENCE_RADIUS_M,
} from '@/features/attendance/api';
export type { GeoConfigDTO, GeoConfigInput } from '@/features/attendance/api';

export {
  useLateFeeRule,
  useSetLateFeeRule,
  useBankAccounts,
  useCreateBankAccount,
  useUpdateBankAccount,
  useDeleteBankAccount,
} from '@/features/fees/api';
export type {
  LateFeeRuleDTO,
  LateFeeRuleInput,
  LateFeeKind,
  BankAccountDTO,
  BankAccountInput,
  UpdateBankAccountInput,
} from '@/features/fees/api';

export { usePolicies, useUpsertPolicy } from '@/features/library/api';
export type { LibraryPolicyDTO, UpsertPolicyInput, BorrowerKind } from '@/features/library/api';

export { useSchools } from '@/features/academic/api';
